import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface AdminRouteProps {
  children: ReactNode;
}

const ALLOWED_DOMAIN = '@jediacademy.com.br';

export const AdminRoute = ({ children }: AdminRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();

  // Loading state
  if (authLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated - redirect to admin login
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  // Check domain restriction
  const email = user.email || '';
  if (!email.endsWith(ALLOWED_DOMAIN)) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <Card className="max-w-md p-8 bg-card border-destructive/30 text-center">
          <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-destructive" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Domínio Não Autorizado</h1>
          <p className="text-muted-foreground mb-6">
            Apenas contas <strong>{ALLOWED_DOMAIN}</strong> podem acessar o painel administrativo.
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
          >
            Voltar ao Início
          </Button>
        </Card>
      </div>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <Card className="max-w-md p-8 bg-card border-destructive/30 text-center">
          <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-destructive" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Acesso Negado</h1>
          <p className="text-muted-foreground mb-6">
            Sua conta foi verificada, mas você não tem permissão de administrador.
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
          >
            Voltar ao Início
          </Button>
        </Card>
      </div>
    );
  }

  // Is admin - render protected content
  return <>{children}</>;
};
